/**
 * Client side of the prescription-intelligence layer: what should the pickers show
 * BEFORE the doctor types anything?
 *
 * For EVERY EMR section (drugs, diagnosis, complaints, investigations, observations,
 * advice, instructions) the server ranks suggestions by three tiers — this doctor's
 * co-occurrence with the current diagnosis/complaint context, everyone's
 * co-occurrence, and the doctor's overall most-used entities as the intelligent
 * fallback. Responses are cached in the IndexedDB prescription_intel store (6h TTL) so
 * repeat opens are instant and dropdowns still auto-load offline; a stale cache beats
 * an empty dropdown, so API failures fall back to whatever was cached last.
 *
 * Two priming paths keep the caches warm off the interaction path:
 *  - primeEmrIntelForSync(): the context-free "most used" set per brand, run as part of
 *    the EMR background sync (so it's guaranteed on session-page open and shows up as a
 *    row in Settings > Update Local Data)
 *  - setRxIntelContext(): re-primes every section for the NEW diagnosis context when the
 *    doctor adds/removes a diagnosis mid-prescription
 */

import { getCPDataWithoutParams } from 'src/clinic/services'
import {
  BACKGROUND_REQUEST_HEADER,
  waitForForegroundNetworkIdle,
} from 'src/consumer/services/networkActivityTracker'
import { getIntelRecordFromIdb, putIntelRecordsInIdb } from 'src/emr/database/indexedDbDao'

const INTEL_TTL_MS = 6 * 60 * 60 * 1000
const PREFETCH_DEBOUNCE_MS = 800
const PRIME_STAGGER_MS = 250

export const INTEL_SOURCE_LABELS = {
  'context-doctor': 'You use this for the selected diagnosis',
  'context-global': 'Commonly used for the selected diagnosis',
  frequent: 'Frequently used by you',
}

// every EMR section with a most-used dropdown, in priming order (drugs first —
// it's the picker doctors reach for most). Values are the backend intel type keys.
export const EMR_INTEL_TYPES = [
  'drug-info',
  'diagnosis',
  'health-complaint',
  'clinical-diagnosis',
  'clinical-symptom',
  'investigation',
  'health-observation',
  'prescription-advice',
  'instruction',
]

let rxContext = { diagnosisIds: [], clinicBrandId: null }
let prefetchTimer = null

/**
 * Published by the prescription page whenever its diagnosis/complaints change (and on
 * open). Re-primes EVERY section's suggestions for the new context (debounced) so blank
 * dropdown opens stay instant mid-prescription.
 */
export function setRxIntelContext({ diagnosisIds, clinicBrandId } = {}) {
  rxContext = {
    diagnosisIds: [...new Set((diagnosisIds || []).filter(Boolean))].sort(),
    clinicBrandId: clinicBrandId || null,
  }
  if (typeof window !== 'undefined') {
    clearTimeout(prefetchTimer)
    prefetchTimer = setTimeout(() => {
      primeEmrIntelSuggestions()
    }, PREFETCH_DEBOUNCE_MS)
  }
}

export function getRxIntelContext() {
  return rxContext
}

function cacheKeyFor(type, ctx) {
  return `${type}__${ctx.clinicBrandId || ''}__${(ctx.diagnosisIds || []).join(',') || 'overall'}`
}

// one network fetch per cache key at a time — repeated opens/primes share it
const inFlightFetches = new Map()

function refreshFromNetwork(type, limit, ctx, { background = false } = {}) {
  const cacheKey = cacheKeyFor(type, ctx)
  if (inFlightFetches.has(cacheKey)) {
    return inFlightFetches.get(cacheKey)
  }
  const fetchPromise = (async () => {
    // priming is background work — let anything the user is doing go first
    if (background) {
      await waitForForegroundNetworkIdle()
    }
    const params = new URLSearchParams({ type, limit: String(limit) })
    if (ctx.clinicBrandId) params.set('clinicBrandId', ctx.clinicBrandId)
    if (ctx.diagnosisIds?.length) params.set('contextIds', ctx.diagnosisIds.join(','))
    const resp = await getCPDataWithoutParams(
      `/api/secure/clinic-practitioner/prescription-intel/suggestions?${params.toString()}`,
      background ? { headers: { [BACKGROUND_REQUEST_HEADER]: '1' } } : {},
    )
    const payload = resp?.data?.data?.prescriptionIntel || resp?.data?.prescriptionIntel
    const items = (payload?.items || [])
      .filter((item) => item?.entity)
      .map((item) => ({ ...item.entity, __intelSource: item.source }))
    putIntelRecordsInIdb([{ id: cacheKey, items, fetchedAt: Date.now() }]).catch(() => {})
    return items
  })()
  inFlightFetches.set(
    cacheKey,
    fetchPromise.finally(() => inFlightFetches.delete(cacheKey)),
  )
  return inFlightFetches.get(cacheKey)
}

/**
 * CACHE-ONLY read for the blank-dropdown prepend: returns the primed suggestions
 * instantly from IndexedDB and NEVER blocks the dropdown on the network. A missing or
 * stale cache kicks a background refresh (polite, foreground-yielding) so the NEXT open
 * has it — the current open just shows whatever is cached (possibly nothing). This is
 * why opening a picker is instant regardless of network/ backend latency.
 */
async function fetchSuggestions(type, limit) {
  const ctx = rxContext
  const cacheKey = cacheKeyFor(type, ctx)

  let cached = null
  try {
    cached = await getIntelRecordFromIdb(cacheKey)
  } catch (error) {
    // cache read failure — fall through to a background warm, return nothing now
  }

  const isFresh = cached && Date.now() - (cached.fetchedAt || 0) < INTEL_TTL_MS
  if (!isFresh) {
    // warm in the background; do NOT await — the dropdown must not wait on the network
    refreshFromNetwork(type, limit, ctx, { background: true }).catch(() => {})
  }
  return cached?.items || []
}

/** Drug suggestions for the Rx picker's empty dropdown state. */
export async function getRxDrugSuggestions({ limit = 15 } = {}) {
  return await fetchSuggestions('drug-info', limit)
}

/** Same intelligence for the other pickers (diagnosis, complaints, investigations...). */
export async function getIntelSuggestions(type, { limit = 15 } = {}) {
  return await fetchSuggestions(type, limit)
}

const defaultReportOption = (entity) => ({
  ...entity,
  value: entity?.id || entity?.title,
  label: entity?.title,
  __intelSource: entity.__intelSource,
})

/**
 * Prepends a report picker's most-used / diagnosis-context suggestions on a blank first
 * open (page 1, no search), deduped against the loaded options. `toOption` adapts the
 * raw entity to that picker's option shape. Best-effort — returns the input unchanged on
 * any failure so the picker always works.
 */
export async function withReportIntelOptionsFirst(
  optionsArray,
  { page, search, intelType, limit = 10, toOption = defaultReportOption } = {},
) {
  if (page !== 1 || search) {
    return optionsArray
  }
  try {
    const suggestions = await getIntelSuggestions(intelType, { limit })
    if (!suggestions?.length) {
      return optionsArray
    }
    const intelOptions = suggestions.map(toOption)
    const intelIds = new Set(intelOptions.map((option) => option?.value).filter(Boolean))
    return [
      ...intelOptions,
      ...(optionsArray || []).filter((option) => !intelIds.has(option?.value)),
    ]
  } catch (error) {
    return optionsArray
  }
}

async function refreshIfColdOrStale(type, ctx, { force = false, limit = 20 } = {}) {
  if (!force) {
    try {
      const cached = await getIntelRecordFromIdb(cacheKeyFor(type, ctx))
      if (cached?.items && Date.now() - (cached.fetchedAt || 0) < INTEL_TTL_MS) {
        return // fresh — nothing to do
      }
    } catch (error) {
      // fall through to fetch
    }
  }
  await refreshFromNetwork(type, limit, ctx, { background: true }).catch(() => {})
}

/**
 * Context-aware prime for the CURRENT rxContext (called when the diagnosis context
 * changes): warms every section's suggestions in the background, staggered and
 * idle-scheduled so it never competes with the page's own loading.
 */
export function primeEmrIntelSuggestions() {
  if (typeof window === 'undefined') {
    return
  }
  const ctx = rxContext
  const kickoff = () => {
    EMR_INTEL_TYPES.forEach((type, index) => {
      setTimeout(() => refreshIfColdOrStale(type, ctx), index * PRIME_STAGGER_MS)
    })
  }
  if (window.requestIdleCallback) {
    window.requestIdleCallback(kickoff, { timeout: 8000 })
  } else {
    setTimeout(kickoff, 2000)
  }
}

/**
 * Sync-driven prime: warms the context-free ("overall") most-used set for every EMR
 * section for each clinic brand. Run as an EMR sync unit so it's guaranteed on
 * session-page open and reports progress into Settings > Update Local Data. Polite
 * (yields to foreground) and skips still-fresh caches unless forced.
 */
export async function primeEmrIntelForSync({ clinicBrandIds, force = false, onProgress } = {}) {
  const brands = clinicBrandIds?.length ? clinicBrandIds : [null]
  const total = brands.length * EMR_INTEL_TYPES.length
  let done = 0
  for (const brand of brands) {
    const ctx = { clinicBrandId: brand, diagnosisIds: [] }
    for (const type of EMR_INTEL_TYPES) {
      await refreshIfColdOrStale(type, ctx, { force })
      done += 1
      onProgress?.(done, total)
    }
  }
  return { primed: done, total }
}
